/**
 * InspectionStepSelection - Step 1: Select vehicles + set date/inspector
 */
import React, { useState, useMemo } from 'react';
import { Search, CheckSquare, Square, Printer, Car } from 'lucide-react';
import { RESOURCE_COLUMNS } from '../../../../config/inspection.js';

export default function InspectionStepSelection({
  vehicles,
  selectedVehicleIds,
  onSelectionChange,
  inspectionDate,
  onDateChange,
  inspectorName,
  onInspectorChange,
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search || search.length < 2) return vehicles;
    const q = search.toLowerCase();
    return vehicles.filter(v =>
      v.license_plate?.toLowerCase().includes(q) ||
      v.make?.toLowerCase().includes(q) ||
      v.model?.toLowerCase().includes(q)
    );
  }, [vehicles, search]);

  const toggleVehicle = (id) => {
    onSelectionChange(
      selectedVehicleIds.includes(id)
        ? selectedVehicleIds.filter(x => x !== id)
        : [...selectedVehicleIds, id]
    );
  };

  const selectAll = () => onSelectionChange(filtered.map(v => v.id));
  const deselectAll = () => onSelectionChange([]);

  const handlePrintBlankGrid = () => {
    const cols = RESOURCE_COLUMNS;
    const rows = selectedVehicleIds.length > 0
      ? vehicles.filter(v => selectedVehicleIds.includes(v.id))
      : vehicles;

    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Fiche Inspection Vierge</title>
    <style>
      body { font-family: sans-serif; font-size: 11px; margin: 20px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: center; }
      th { background: #f3f4f6; font-size: 10px; }
      .veh { text-align: left; font-weight: bold; }
      h1 { font-size: 16px; margin-bottom: 4px; }
      .meta { color: #666; margin-bottom: 12px; font-size: 12px; }
    </style></head><body>
    <h1>Inspection Groupee — Fiche Vierge</h1>
    <div class="meta">Date: ${inspectionDate || '___/___/______'} | Inspecteur: ${inspectorName || '________________________'}</div>
    <table>
      <thead>
        <tr>
          <th>Vehicule</th>
          <th>km</th>
          ${cols.map(c => `<th title="${c.label}">${c.shortLabel}</th>`).join('')}
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        ${rows.map(v => `<tr>
          <td class="veh">${v.license_plate} ${v.make || ''} ${v.model || ''}</td>
          <td style="width:60px"></td>
          ${cols.map(() => '<td style="width:36px"></td>').join('')}
          <td style="width:120px"></td>
        </tr>`).join('')}
      </tbody>
    </table>
    <div style="margin-top:12px;font-size:10px;color:#999">
      Legende: ✓ = Bon | ~ = Acceptable | ! = Attention | ✗ = Critique
    </div>
    </body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <div className="space-y-6">
      {/* Date + Inspector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date d'inspection</label>
          <input
            type="date"
            value={inspectionDate}
            onChange={(e) => onDateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Inspecteur</label>
          <input
            type="text"
            value={inspectorName}
            onChange={(e) => onInspectorChange(e.target.value)}
            placeholder="Nom de l'inspecteur"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={handlePrintBlankGrid}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            Imprimer fiche vierge
          </button>
        </div>
      </div>

      {/* Search + Select all */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par plaque, marque, modèle..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button onClick={selectAll} className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg">
          Tout selectionner
        </button>
        <button onClick={deselectAll} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">
          Tout deselectionner
        </button>
        <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
          {selectedVehicleIds.length} selectionne{selectedVehicleIds.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Vehicle cards grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {filtered.map(v => {
          const selected = selectedVehicleIds.includes(v.id);
          return (
            <div
              key={v.id}
              onClick={() => toggleVehicle(v.id)}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                selected
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-sm font-bold text-gray-900">
                  {v.license_plate}
                </span>
                {selected
                  ? <CheckSquare className="w-4 h-4 text-blue-600" />
                  : <Square className="w-4 h-4 text-gray-300" />
                }
              </div>
              <div className="text-xs text-gray-500 truncate">
                {v.make} {v.model}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {v.fuel_type === 'diesel' ? 'Diesel' : v.fuel_type === 'essence' ? 'Essence' : v.fuel_type || ''}
                {v.mileage ? ` · ${(v.mileage / 1000).toFixed(0)}k km` : ''}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          <Car className="w-12 h-12 mx-auto mb-2 opacity-40" />
          <p>Aucun vehicule ne correspond a la recherche</p>
        </div>
      )}
    </div>
  );
}
